import { type FC, useRef } from "react";

import { ChevronRight } from "lucide-react";

import { Button } from "@/shared/components/ui/button";

import uploadGraphic from "@/shared/assets/illustrations/upload-graphic.svg";
import takePicture from "@/shared/assets/illustrations/take-picture.svg";

interface UploadMethodCardsProps {
  onFileSelected: (file: File) => void;
  onSelectPhone: () => void;
  hasFile?: boolean;
  variant?: "stacked" | "side-by-side";
  accept?: string;
}

export const UploadMethodCards: FC<UploadMethodCardsProps> = ({
  onFileSelected,
  onSelectPhone,
  hasFile = false,
  variant = "stacked",
  accept = ".pdf,.jpg,.jpeg,.png,.heic,.heif",
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const fileInput = (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      className="hidden"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onFileSelected(file);
        e.target.value = "";
      }}
    />
  );

  if (variant === "side-by-side") {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button
          variant="outline"
          onClick={() => inputRef.current?.click()}
          className="flex h-auto flex-col items-center justify-center gap-3 rounded-lg p-6"
        >
          <img
            src={uploadGraphic}
            alt="Upload a file"
            className="h-[90px] w-[90px] object-contain"
          />
          <p className="text-base font-medium text-primary">Choose a file</p>
          {fileInput}
        </Button>
        <Button
          variant="outline"
          onClick={onSelectPhone}
          className="hidden md:flex h-auto flex-col items-center justify-center gap-3 rounded-lg p-6"
        >
          <img
            src={takePicture}
            alt="Take a picture with phone"
            className="h-[90px] w-[90px] object-contain"
          />
          <p className="text-base font-medium text-primary">Take a picture with your phone</p>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Button
        variant="outline"
        onClick={() => inputRef.current?.click()}
        className="flex h-auto w-full flex-col items-center justify-center gap-3 rounded-lg p-8"
      >
        <img src={uploadGraphic} alt="Upload a file" className="h-[90px] w-[90px] object-contain" />
        <div className="flex flex-col items-center gap-1">
          <p className="text-base font-medium text-primary">Choose a file</p>
          <p className="text-sm text-secondary">Click here to choose a file, or drag it in</p>
        </div>
        {fileInput}
      </Button>

      {!hasFile && (
        <div className="hidden md:flex flex-col gap-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-primary" />
            <span className="text-xs text-tertiary">OR</span>
            <div className="flex-1 border-t border-primary" />
          </div>

          <Button
            variant="outline"
            onClick={onSelectPhone}
            className="flex h-auto items-center gap-3 rounded-lg p-4"
          >
            <img
              src={takePicture}
              alt="Take a picture with phone"
              className="h-12 w-12 object-contain shrink-0"
            />
            <p className="flex-1 text-sm font-medium text-primary text-left">
              Take a picture with your phone
            </p>
            <ChevronRight className="h-4 w-4 shrink-0 text-tertiary" />
          </Button>
        </div>
      )}
    </div>
  );
};
